<script lang="ts">
  import { onMount, setContext } from "svelte";
  import { Writable, writable, get } from "svelte/store";
  import Layout from "./Layout.svelte";
  import AuthFormHeader from "../common/AuthFormHeader.svelte";
  import ProgressBar from "./ProgressBar.svelte";
  import PersonalInformation from "./steps/personal-information/PersonalInformation.svelte";
  import VerifyEmail from "./steps/verify-email/VerifyEmail.svelte";
  import SelectAccountType from "./steps/account-type/AccountType.svelte";
  import SelectPlan from "./steps/select-plan/SelectPlan.svelte";
  import Payment from "./steps/payment/Payment.svelte";
  import Summary from "./steps/payment/Summary.svelte";
  import Questions from "./steps/questions/Questions.svelte";
  import { CREATE_NEW_ACCOUNT_CONTEXT_NAME, PLAN_LOCAL_STORAGE_KEY } from "./utils";
  import { authQuestions } from "../../../app/store/store-auth";
  import {
    AccountType,
    PlanTier,
    PlanRecurringPeriod,
    getPlanPerSeatCost,
    getPlanBaseCost,
  } from "../../../app/store/store-plan";
  import type {
    CreateNewAccountPage,
    CreateNewAccountPageName,
    CreateNewAccountContext,
    PageData,
    ProgressStep,
  } from "./type";
  import pageUrl, { redirectTo } from "../../../PageUrl";
  import { userInfoStore, setUserInfo, isLoggedIn, currentUserStore, isCurrentUserLoaded } from "../../../app/store/store-user";
  import { getPlanInfo, updateUserInfo } from "../../../app/api";

  const pagesStore: Writable<CreateNewAccountPage[]> = writable([
    {
      name: "personal_information",
      step: 1,
      component: PersonalInformation,
      values: {},
    },
    {
      name: "verify_email",
      step: 1,
      component: VerifyEmail,
      values: {},
    },
    {
      name: "account_type",
      step: 2,
      component: SelectAccountType,
      values: {
        accountType: "individual",
        organizationName: "",
      },
    },
    {
      name: "select_plan",
      step: 2,
      component: SelectPlan,
      values: {
        duration: "yearly",
        numberOfLicenses: 1,
        plan: "business",
      },
    },
    {
      name: "payment",
      step: 3,
      component: Payment,
      sideComponent: Summary,
      values: {},
    },
    // {
    //   name: "questions",
    //   step: 3,
    //   component: Questions,
    //   values: {
    //     questions: [],
    //   },
    // },
  ]);
  const totalCost = writable(0);
  // const pageToRedirectAfterRegister = pageUrl.SIGNIN;

  const params = new URLSearchParams(document.location.search);
  const currentPageName = params.get("page");
  let currentPageIndex: number = Math.max(
    get(pagesStore).findIndex((p) => p.name === currentPageName),
    0
  );
  let isPaid: boolean = false; // prevent navigating to previous step if the plan is paid
  let progressSteps: ProgressStep[] = [
    {
      pageName: "personal_information",
      label: "General Info.",
      finish: false,
      canNavigate: true,
    },
    {
      pageName: "account_type",
      label: "Plans Info.",
      finish: false,
      canNavigate: false,
    },
    {
      pageName: "payment",
      label: "Payment Info.",
      finish: false,
      canNavigate: false,
    },
  ];
  const newAccountContext = getNewAccountContext();

  function getNewAccountContext(): CreateNewAccountContext {
    const getPageData = (name: CreateNewAccountPageName): PageData => {
      return get(pagesStore).find((p) => p.name === name)?.values;
    };

    const getFirstName = (): string => {
      const pageData = getPageData("personal_information");
      return pageData.firstname ?? "";
    };

    const getLastName = (): string => {
      const pageData = getPageData("personal_information");
      return pageData.lastname ?? "";
    };

    const getEmail = (): string => {
      const pageData = getPageData("personal_information");
      return pageData.email ?? "";
    };

    // in case a user information has been update from server
    // need to update page data accordingly
    const updatePersonalInforFromUserData = () => {
      if (!userInfo) return;
      const pageData = getPageData("personal_information");
      pageData.firstname = userInfo.firstName;
      pageData.lastname = userInfo.lastName;
      pageData.email = userInfo.email;
    };

    const getPassword = (): string => {
      const pageData = getPageData("personal_information");
      return pageData.password ?? "";
    };

    const getAccountType = (): AccountType => {
      const pageData = getPageData("account_type");
      return (pageData.accountType.toString() as AccountType) ?? "individual";
    };

    const setAccountType = (type: AccountType) => {
      const pageData = getPageData("account_type");
      pageData.accountType = type;
      notifyPagesStoreChange();
      savePlanLocalStorage();
    }

    const getOrganizationName = (): string => {
      const pageData = getPageData("account_type");
      return pageData.organizationName.toString();
    };

    const setOrganizationName = (name: string) => {
      const pageData = getPageData("account_type");
      pageData.organizationName = name;
      notifyPagesStoreChange();
      savePlanLocalStorage();
    }

    const getPlanDuration = (): PlanRecurringPeriod => {
      const pageData = getPageData("select_plan");
      return pageData && pageData.duration === "monthly" ? "monthly" : "yearly";
    };

    const setPlanDuration = (value: PlanRecurringPeriod) => {
      const pageData = getPageData("select_plan");
      pageData.duration = value;
      notifyPagesStoreChange();
      savePlanLocalStorage();
    };

    const getNumberOfLicenses = (): number => {
      const pageData = getPageData("select_plan");
      return parseInt(pageData.numberOfLicenses.toString()) ?? 1;
    };

    const setNumberOfLicenses = (num: number) => {
      const pageData = getPageData("select_plan");
      pageData.numberOfLicenses = num;
      notifyPagesStoreChange();
      savePlanLocalStorage();
    }

    const getLicensePrice = (): number => {
      const planTier = getPlanTier();
      const accountType = getAccountType();
      const duration = getPlanDuration();
      return accountType === "organization"
        ? getPlanPerSeatCost(planTier, duration)
        : 0;
    };

    const getPlanTier = (): PlanTier => {
      const pageData = getPageData("select_plan");
      return pageData.plan.toString() as PlanTier;
    };

    const setPlanTier = (v: PlanTier) => {
      const pageData = getPageData("select_plan");
      pageData.plan = v;
      notifyPagesStoreChange();
      savePlanLocalStorage();
    };

    const getPlanCost = (planTier?: PlanTier): number => {
      planTier = planTier || getPlanTier();
      const accountType = getAccountType();
      const duration = getPlanDuration();
      return getPlanBaseCost(accountType, planTier, duration);
    };

    const addAuthQuestionAndAnswer = (question: string, answer: string) => {
      const pageData = getPageData("questions");
      pageData.questions.push({ question, answer });
    };

    const notifyPagesStoreChange = () => {
      pagesStore.update((v) => v);
    };

    const loadPlanLocalStorage = () => {
      let planData = localStorage.getItem(PLAN_LOCAL_STORAGE_KEY);
      if (planData) {
        const data = JSON.parse(planData);
        const { accountType, organizationName, duration, numberOfLicenses, plan } = data;
        if (accountType) setAccountType(accountType);
        if (organizationName) setOrganizationName(organizationName);
        if (duration) setPlanDuration(duration);
        if (numberOfLicenses) setNumberOfLicenses(numberOfLicenses);
        if (plan) setPlanTier(plan);
      }
    };

    const savePlanLocalStorage = () => {
      const planData = {
        accountType: getAccountType(),
        organizationName: getOrganizationName(),
        duration: getPlanDuration(),
        numberOfLicenses: getNumberOfLicenses(),
        plan: getPlanTier(),
      };
      localStorage.setItem(PLAN_LOCAL_STORAGE_KEY, JSON.stringify(planData));
    }

    loadPlanLocalStorage();

    return {
      pages: pagesStore,
      totalCost,
      onNext,
      onPrevious,
      getFirstName,
      getLastName,
      getEmail,
      getPassword,
      updatePersonalInforFromUserData,
      getAccountType,
      getOrganizationName,
      getPageData,
      getPlanDuration,
      setPlanDuration,
      getNumberOfLicenses,
      getLicensePrice,
      getPlanTier,
      setPlanTier,
      getPlanCost,
      addAuthQuestionAndAnswer,
    };
  }

  function onNextPageRedirectHandler() {
    if (
      pages[currentPageIndex].name === "personal_information" ||
      pages[currentPageIndex].name === "verify_email"
    ) return;

    setUserInfo(userInfo); // trigger redirect back to ui page if needed
  }

  function onNext(values: PageData, override?: boolean) {
    if (currentPageIndex >= pages.length || currentPageIndex < 0) return;
    const currentPage = pages[currentPageIndex];
    if (!override) {
      currentPage.values = { ...currentPage.values, ...values };
    } else {
      currentPage.values = { ...values };
    }

    setProgressPageFinish(currentPage.name); // Current page information has been filled
    if (currentPage.name === "payment") isPaid = true;

    if (currentPageIndex < pages.length - 1) {
      currentPageIndex++;
      setProgressPageCanNavigate(pages[currentPageIndex].name); // can navigate to this page because the previous step is done
      // scroll to top in case opening a new form
      document.body.scrollTo({ top: 0, left: 0 });
      onNextPageRedirectHandler();
    } else {
      // When user info is updated, it will automatically redirect the page
      // to the correct pages that is store in session storage
      updateUserInfo();
    }
  }

  function onPrevious() {
    if (currentPageIndex > 0) currentPageIndex--;
    // scroll to top in case opening a new form
    document.body.scrollTo({ top: 0, left: 0 });
  }

  function setProgressPageFinish(pageName: CreateNewAccountPageName) {
    const page = progressSteps.find((p) => p.pageName === pageName);
    if (page) {
      page.finish = true;
      progressSteps = progressSteps; // force update progress bar
    }
  }

  function setProgressPageCanNavigate(pageName: CreateNewAccountPageName) {
    const page = progressSteps.find((p) => p.pageName === pageName);
    if (page) {
      page.canNavigate = true;
      progressSteps = progressSteps; // force update progress bar
    }
  }

  function goToPage(pageName: CreateNewAccountPageName) {
    // prevent go to previous page if the plan has been paid
    if (isPaid) return;

    let step = progressSteps.find((s) => s.pageName === pageName);
    if (step && step.canNavigate) {
      let index = pages.findIndex((p) => p.name === step.pageName);
      if (index !== -1) currentPageIndex = index;
    }
  }

  setContext(CREATE_NEW_ACCOUNT_CONTEXT_NAME, newAccountContext);
  $: pages = $pagesStore;
  $: currentUser = $currentUserStore;
  $: userInfo = $userInfoStore;
  $: images = authQuestions.map((q) => q.image);
  $: if ($isLoggedIn) {
    getPlanInfo({});
  }
  $: if (currentUser && currentUser.emailVerified) {
    setProgressPageCanNavigate("personal_information");
    setProgressPageFinish("personal_information");
    setProgressPageCanNavigate("account_type");
  } else if ($isCurrentUserLoaded) {
    // not allow to open plan page if not have an account yet
    currentPageIndex = 0;
  }
</script>

<Layout>
  <div class="create-acount">
    <div class="auth-form-container form-big">
      <div class="form-content">
        <AuthFormHeader title="Create New Account" />

        <div class="pt-2">
          <ProgressBar
            currentStep={pages[currentPageIndex].step}
            {progressSteps}
            {goToPage}
          />
        </div>

        <svelte:component this={pages[currentPageIndex].component} />
      </div>
    </div>

    {#if pages[currentPageIndex].sideComponent}
      <div
        class="side-block"
        class:payment={pages[currentPageIndex].name === "payment"}
      >
        <svelte:component this={pages[currentPageIndex].sideComponent} />
      </div>
    {/if}
  </div>
</Layout>

<!-- preload auth question image -->
<div class="hidden w-0 h-0">
  {#each images as imageUrl}
    <img src={imageUrl} alt="" width="0px" height="0px" />
  {/each}
</div>

<style lang="postcss">
  .auth-form-container.form-big {
    @apply w-full px-2.5 m-0;
    max-width: 740px;
  }

  .auth-form-container.form-big .form-content {
    width: calc(100%);
    margin: 0px;
  }

  .create-acount {
    @apply flex flex-col items-start mx-auto;
    max-width: 740px;
  }

  .side-block {
    @apply relative -mt-10 pl-2.5;
    width: fit-content;
  }

  .side-block.payment {
    width: min(calc(100% - 20px), 360px);
  }

  @media (min-width: 1000px) {
    .create-acount {
      @apply flex flex-row items-start justify-center;
      max-width: unset;
    }

    .side-block {
      @apply m-0 p-0 pt-14 pr-2.5;
    }
  }
</style>
